import React, { useMemo } from 'react';
import messages from 'messages';
import { useRouter } from 'next/router';
import { useT } from '@transifex/react';
import { Button, ResultContent } from '@raisin/components-lib';
import Monitor from '@raisin/components-lib/Icons/Monitor';
import PermIdentity from '@raisin/components-lib/Icons/PermIdentity';
import QuestionMark from '@raisin/components-lib/Icons/QuestionMark';
import RemoveRedEye from '@raisin/components-lib/Icons/RemoveRedEye';
import { Layout } from '@raisin/cp-layout';
import {
  getRelativePathWithLocalePrefix,
  isAuthenticated,
  LEARN_MORE_LINKS_BY_LOCALES,
  LINKS,
  getUSPostLoginBaseUrl,
} from '@raisin/cp-utils';
import { buildCanonicalURL } from 'utils/bankPageUtils';
import * as S from './styles';

export const NotFoundPageComponent = ({ locale, layout, translations }) => {
  const router = useRouter();
  const t = useT();

  const fullURL = useMemo(
    () => buildCanonicalURL(router, locale, process.env.NEXT_PUBLIC_ENVIRONMENT_NAME),
    [locale, router],
  );

  const links = useMemo(
    () => [
      {
        icon: <Monitor color="blue60" />,
        title: t(messages.notFoundPageFirstLink),
        href:
          locale.toLowerCase() === 'en-us'
            ? `${getUSPostLoginBaseUrl()}/auth/login`
            : `${fullURL}#/Dashboard/`,
        testId: 'dashboard-link',
      },
      {
        icon: <PermIdentity color="blue60" />,
        title: t(messages.notFoundPageSecondLink),
        href:
          locale.toLowerCase() === 'en-us'
            ? `${getUSPostLoginBaseUrl()}/auth/register`
            : `${fullURL}register/`,
        testId: 'register-link',
      },
      {
        icon: <RemoveRedEye color="blue60" />,
        title: t(messages.notFoundPageThirdLink),
        href: `${fullURL}${LEARN_MORE_LINKS_BY_LOCALES[locale]}/`,
        testId: 'learn-more-link',
      },
    ],
    [fullURL, t, locale],
  );

  const offersPageCTAClicked = () => {
    const url = isAuthenticated() ? LINKS.PRODUCTS.DEFAULT : '/';
    router.push(getRelativePathWithLocalePrefix({ path: url, fallbackLocale: locale }));
  };

  return (
    <Layout
      {...{
        layout,
        pageTitle: t(messages.notFoundPageDocumentTitle),
        footerGap: true,
      }}
      isPortal={false}
      isAuthRequired={false}
      isBypassAuthContext={false}
      pageMessages={translations}
    >
      <S.ErrorPageContainer>
        <S.ErrorContent>
          <ResultContent
            heading={t(messages.notFoundPageTitle)}
            content={t(messages.notFoundPageDescription)}
            icon={<QuestionMark color={'black60'} />}
            actionTitle={t(messages.notFoundPageMainCTAText)}
            buttonMode="primary"
            onActionClick={offersPageCTAClicked}
            withTransparentBackground={true}
          />
          <S.Links>
            {links.map((link) => (
              <Button
                key={link.title}
                testId={link.testId}
                href={link.href}
                mode="primaryText"
                startIcon={link.icon}
              >
                {link.title}
              </Button>
            ))}
          </S.Links>
        </S.ErrorContent>
      </S.ErrorPageContainer>
    </Layout>
  );
};
